import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  Container,
  CopyToClipboard,
  ExpandableSection,
  Header,
  Link,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationLinkInput,
  InvestigationSuggestion,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  useInvestigationSuggestions,
  useInvestigationTrackStarsRunPatterns,
} from "../../hooks/useInvestigations";
import { TrackStarsRunResult } from "./TrackStarsRunResult";
import { TrackStarsSuggestionCard } from "./TrackStarsSuggestionCard";
import {
  formatRelativeTime,
  investigationPath,
  isTerminal,
} from "./investigationUtils";
import {
  describeFix,
  earlierReportLabel,
  shortPatternId,
  suggestionKey,
  suggestionReportId,
} from "./trackstarsText";
import { WidenRuleModal, widenCandidates } from "./WidenRuleModal";
import { SafeExternalLink } from "./SafeExternalLink";

/** What "Use as proposed conclusion" hands to the conclusion form. */
export interface ProposedConclusion {
  summary: string;
  rootCause?: string;
  fix?: string;
}

/** The conclusion TrackStars' analysis amounts to, in the shape of our conclusion form. */
export const proposalFor = (s: InvestigationSuggestion): ProposedConclusion => {
  const detail = s.recommendedAction?.description?.trim();
  const plain = describeFix({
    ...s,
    starter: { ...s.starter, fix: undefined },
  });
  return {
    summary: s.description,
    rootCause: s.rootCause?.summary || undefined,
    fix: detail ? `${plain}. ${detail}` : plain,
  };
};

/** Where TrackStars is set up and run. */
export const TRACKSTARS_WIKI_URL =
  "https://w.amazon.com/bin/view/AIV/FinSys/TRACK/Systems/TrackStars/";

/**
 * Starter link labels carry TrackStars' full ids ("TrackStars <report>-PAT-001 (<report>)"); the
 * button needs just enough to tell links apart.
 */
export const shortLinkLabel = (label: string): string => {
  const short = label
    .replace(/\s*\([^)]*\)\s*$/, "")
    .replace(/\S*-(PAT-\d+)\b/gi, "$1")
    .replace(/^Suggested\s+/i, "")
    .trim();
  const text = short ? short[0].toUpperCase() + short.slice(1) : label;
  return text.length > 40 ? `${text.slice(0, 39)}…` : text;
};

/** Items of this investigation a pattern of its own run matched (0 when unknown). */
export const runPatternItems = (s: InvestigationSuggestion): number =>
  s.runItems ?? 0;

/** The line under a run card's headline: how much of this case it explains. */
export const runPatternNote = (s: InvestigationSuggestion): string =>
  `${runPatternItems(
    s
  ).toLocaleString()} of these items · From this investigation's run`;

const RELATION_LABEL: Record<string, string> = {
  origin: "This investigation was started from this pattern",
  covers: "This investigation's rule covers the whole pattern",
  partial: "This investigation holds part of the pattern",
};

/** Patterns of the latest TrackStars report that touch this investigation, origin first. */
export const patternsForInvestigation = (
  suggestions: InvestigationSuggestion[] | undefined,
  investigationId: string
): Array<{ suggestion: InvestigationSuggestion; relation: string }> =>
  (suggestions ?? [])
    .flatMap((suggestion) => {
      const match = suggestion.ownership.investigations.find(
        (inv) => inv.investigationId === investigationId
      );
      return match?.relation
        ? [{ suggestion, relation: match.relation as string }]
        : [];
    })
    .sort(
      (a, b) =>
        ["origin", "covers", "partial"].indexOf(a.relation) -
        ["origin", "covers", "partial"].indexOf(b.relation)
    );

/**
 * The prompt a person pastes into their coding agent (Kiro etc.) on a Cloud Desktop with TrackStars
 * set up: it drives TrackStars through the `dlq-analysis` skill in TrackStarsDevDocs (wording from
 * the TrackStars team). Works for categorised and uncategorised investigations. The last paragraph
 * asks for the structured write-back so the TrackStars tab can show coverage and split patterns;
 * it needs only the investigation id (the report completes the waiting run, or records a new one).
 */
/**
 * The AIM MCP bundle serving this Lusca deployment: a personal dev stack (`<alias>.amazon.com`
 * dev server or `<alias>.people.amazon.dev`) is `lusca-search-<alias>-mcp`; anything else is the
 * shared `lusca-search-mcp`.
 */
export const luscaMcpServer = (hostname: string) => {
  const personal = /^([a-z0-9]+)\.(?:amazon\.com|people\.amazon\.dev)$/.exec(
    hostname
  );
  return personal ? `lusca-search-${personal[1]}-mcp` : "lusca-search-mcp";
};

export const trackStarsAgentPrompt = (
  investigationUrl: string,
  investigationId: string,
  stage: string,
  mcpServer = "lusca-search-mcp"
) =>
  `Using the dlq-analysis skill in TrackStarsDevDocs
(https://code.amazon.com/packages/TrackStarsDevDocs/trees/mainline),
investigate the ${stage} DLQ case at ${investigationUrl}.

Read and update the investigation only through the ${mcpServer} MCP server. If it
does not list the investigations tools, stop and tell the user; do not edit MCP
bundle configs to point elsewhere.

If TrackStarsDevDocs is unavailable locally, add or sync the package first.
Read the investigation, its frozen population, timeline and existing comments
before starting. Do not repeat completed work.

If the population is categorized, run TrackStars for the exact error_category
shown in the investigation. If it is uncategorized, run TrackStars across the
uncategorized population for the selected window, then focus the follow-up
investigation on the population in the investigation. Clearly separate the
target pattern from unrelated patterns found during the same run, and if that pattern is not in the report, mention that but still provide the report and continue with dashboard publication, as it could help identify other uncategorized issues.

Determine the analysis window from the investigation's frozen population or
source TrackStars report. Use the narrowest UTC window covering those items.
If the frozen population has 50 items or fewer, pass their transaction IDs with
--orders / --orders-file instead of a day window. If older history is needed to establish recurrence, query it separately without
changing the primary population. State the chosen window and why.

Publish and validate the DOCX and dashboard report. Add a concise findings
update with supporting evidence and report links to the investigation.
Write every investigation comment in Markdown (it is rendered): bold labels or
### headings per section, bullet lists for findings, backticks around
identifiers, file paths, queues, error categories and transaction IDs, fenced
code blocks for multi-line code or .when() clauses, and [text](url) links.

Run TrackStars without --raise-cr or --create-backlog. If evidence supports a
code change, implement and test it separately, then raise an unpublished draft
CR yourself.

If the findings justify tracking work, read TrackStars' backlog rules for
deduplicating and grouping related patterns by stable failure family. Follow the
taskei-hierarchy skill in the PVFinsysTrackGenAIContext package (git pull the
package first; a fetch alone leaves an old checkout without it).

Before creating anything, show the user the proposed title, description,
acceptance criteria, item type and parent. After explicit approval, create only
the approved Taskei items using the completed analysis; do not rerun TrackStars.

Do not redrive transactions or make production changes without explicit approval.

When the TrackStars report is final, write it back once through the ${mcpServer} MCP server:
POST investigations/${investigationId}/trackstars-report (stage=${stage}) with body
{"report": <the TrackStars DashboardReport JSON>}. Lusca counts each pattern's items in this
population and shows every pattern as a card on the investigation's TrackStars tab, built only
from the report. So before writing it back, make sure every pattern in pattern_analysis has:
- root_cause.summary (one or two sentences), root_cause.layer and root_cause.evidence
- recommended_action.type and recommended_action.description (the concrete fix)
- suggested_category and suggested_when_clause (the exact .when(...) clause)
- sample_transactions (up to 5, with transaction_id or order_id)
If your follow-up investigation corrected TrackStars (a wrong GL, queue, onset or cause), put the
corrected root cause and fix in those fields, not only in a comment.
`;

/**
 * Created from `error_category = uncategorized`: TrackStars is asked to categorise the population
 * (find and name its patterns) rather than root-cause one known failure.
 */
export const isUncategorisedInvestigation = (investigation: Investigation) =>
  investigation.categoryScope?.selected === "uncategorized";

/** What a TrackStars run on this investigation is for — decides the label and the prompt's goal. */
export type TrackStarsJob = "categorise" | "fix" | "rootcause";

/** The investigation's own TrackStars run is back and scored. */
export const hasCompletedTrackStarsRun = (investigation: Investigation) =>
  investigation.trackstarsRun?.status === "COMPLETED" &&
  !!investigation.trackstarsRun.result;

/**
 * Every item is explained by the patterns of the investigation's own run: categorising again
 * would add nothing (the DLQ keeps them `uncategorized` until the categoriser change ships).
 */
export const isFullyCategorisedByTrackStars = (
  investigation: Investigation
) => {
  const result = investigation.trackstarsRun?.result;
  return (
    hasCompletedTrackStarsRun(investigation) &&
    !!result &&
    result.totalItems > 0 &&
    result.explainedItems >= result.totalItems
  );
};

export const trackStarsJob = (investigation: Investigation): TrackStarsJob =>
  isUncategorisedInvestigation(investigation) &&
  !isFullyCategorisedByTrackStars(investigation)
    ? "categorise"
    : investigation.origin?.source === "trackstars"
    ? "fix"
    : "rootcause";

export const TRACKSTARS_JOBS: Record<
  TrackStarsJob,
  { label: string; header: string; explains: string }
> = {
  categorise: {
    label: "Categorise with TrackStars",
    header: "Categorise these items with TrackStars",
    explains:
      "Finds and names the failure patterns in these uncategorised items, with a title and root cause for each. Split them out afterwards on the TrackStars tab.",
  },
  fix: {
    label: "Fix with TrackStars",
    header: "Work towards a fix with TrackStars",
    explains:
      "TrackStars already diagnosed this pattern. It builds on that: proposes the conclusion and fix, a draft CR if code must change, and a redrive plan.",
  },
  rootcause: {
    label: "Root-cause with TrackStars",
    header: "Root-cause this population with TrackStars",
    explains:
      "Finds the root cause of these items and proposes a conclusion and fix.",
  },
};

/** The first line of the prompt: which of the three jobs this run is. */
export const trackStarsGoal = (
  investigation: Investigation,
  job: TrackStarsJob = trackStarsJob(investigation)
): string => {
  switch (job) {
    case "categorise":
      return "Goal: CATEGORISE. These items are uncategorised: find and name their failure patterns (error_category + .when() clause each), and propose a title and root cause for each pattern.";
    case "fix":
      return `Goal: FIX. TrackStars already diagnosed this population as pattern ${
        investigation.origin?.patternId ?? "?"
      }${
        investigation.origin?.reportId
          ? ` (report ${investigation.origin.reportId})`
          : ""
      }. Do not re-diagnose from scratch: verify that diagnosis still holds for these items, then propose the conclusion and fix, a draft CR if code must change, and a redrive plan.`;
    default:
      return "Goal: ROOT-CAUSE. Find the root cause of these items and propose a conclusion and fix.";
  }
};

interface RunTrackStarsProps {
  stage: Stage;
  investigation: Investigation;
  /**
   * The TrackStars tab's re-run button ("Re-run categorisation" etc.): repeats the job of the case's last run. Without it
   * (the page header) the button only shows until the case has a completed run of its own.
   */
  rerun?: boolean;
}

/** The job a re-run repeats: the last run's own (a categorisation stays a categorisation). */
/** What the tab's re-run button says: which job it repeats. */
export const RERUN_LABEL: Record<TrackStarsJob, string> = {
  categorise: "Re-run categorisation",
  rootcause: "Re-run root-cause",
  fix: "Re-run fix analysis",
};

export const rerunJob = (investigation: Investigation): TrackStarsJob =>
  investigation.trackstarsRun?.mode === "categorise"
    ? "categorise"
    : trackStarsJob(investigation);

/**
 * "Categorise / Root-cause / Fix with TrackStars" in the page header, or "Re-run <job>" on
 * the TrackStars tab once the case has a run: the native copy button copies the prompt; nothing
 * is recorded or run from here. The run appears on the investigation when TrackStars writes its
 * report back.
 */
export const RunTrackStarsButton: React.FC<RunTrackStarsProps> = ({
  stage,
  investigation,
  rerun = false,
}) => {
  const job = rerun ? rerunJob(investigation) : trackStarsJob(investigation);
  const categorise = job === "categorise";
  // After a categorisation the re-run also offers the next step (root-cause, or fix for a case
  // started from a TrackStars pattern).
  const nextJob: TrackStarsJob | null =
    rerun && categorise
      ? investigation.origin?.source === "trackstars"
        ? "fix"
        : "rootcause"
      : null;
  const [openJob, setOpenJob] = useState<TrackStarsJob | null>(null);
  // Once the case has its own completed run, the tab holds the result and the re-run.
  if (!rerun && hasCompletedTrackStarsRun(investigation)) return null;
  const open = openJob !== null;
  const copy = TRACKSTARS_JOBS[openJob ?? job];
  const prompt = `${trackStarsGoal(
    investigation,
    openJob ?? job
  )}\n\n${trackStarsAgentPrompt(
    `${window.location.origin}${investigationPath(investigation.id, stage)}`,
    investigation.id,
    stage,
    luscaMcpServer(window.location.hostname)
  )}`;
  const setOpen = (value: boolean) => setOpenJob(value ? job : null);
  return (
    <>
      {nextJob ? (
        <ButtonDropdown
          variant="normal"
          expandToViewport
          mainAction={{
            text: RERUN_LABEL[job],
            iconName: "refresh",
            onClick: () => setOpenJob(job),
          }}
          items={[{ id: nextJob, text: TRACKSTARS_JOBS[nextJob].label }]}
          ariaLabel="More TrackStars runs"
          onItemClick={() => setOpenJob(nextJob)}
        />
      ) : (
        <Button
          iconName={rerun ? "refresh" : "gen-ai"}
          variant={categorise && !rerun ? "primary" : "normal"}
          onClick={() => setOpen(true)}
        >
          {rerun ? RERUN_LABEL[job] : TRACKSTARS_JOBS[job].label}
        </Button>
      )}
      {open && (
        <Modal
          visible
          onDismiss={() => setOpen(false)}
          header={copy.header}
          footer={
            <Box float="right">
              <Button variant="primary" onClick={() => setOpen(false)}>
                Done
              </Button>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <Box>{copy.explains}</Box>
            <CopyToClipboard
              variant="button"
              copyButtonText="Copy prompt"
              textToCopy={prompt}
              copySuccessText="Prompt copied"
              copyErrorText="Prompt failed to copy"
            />
            <Box color="text-body-secondary" fontSize="body-s">
              Paste into Kiro (or your agent) on a Cloud Desktop with TrackStars
              set up.
            </Box>
            <Box color="text-body-secondary" fontSize="body-s">
              First time?{" "}
              <Link href={TRACKSTARS_WIKI_URL} external>
                Setup and credentials
              </Link>
            </Box>
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
};

/** The latest TrackStars run once its report is back: counting / failed / result. */
export const TrackStarsRunStatus: React.FC<{
  investigation: Investigation;
  now?: number;
  /** Only the states that need attention (counting / failed); the result lives on the tab. */
  pendingOnly?: boolean;
}> = ({ investigation, now, pendingOnly }) => {
  const run = investigation.trackstarsRun;
  if (!run || run.status === "REQUESTED") return null;
  if (pendingOnly && run.status === "COMPLETED") return null;
  const kind = run.mode === "categorise" ? "categorisation" : "analysis";
  const { result } = run;
  // Patterns that matched none of the items explain nothing; don't count them.
  const matching = result?.patterns?.length
    ? result.patterns.filter((p) => (p.items ?? 0) > 0).length
    : result?.patternCount ?? 0;
  return (
    <span
      data-testid="trackstars-run-status"
      title={`Run ${run.runId} · ${run.requestedAt}`}
    >
      {run.status === "SCORING" && (
        <StatusIndicator type="loading">
          TrackStars {kind} received — counting its patterns in this population…
        </StatusIndicator>
      )}
      {run.status === "FAILED" && (
        <StatusIndicator type="error">
          TrackStars run could not be scored
          {run.error ? `: ${run.error}` : ""}
        </StatusIndicator>
      )}
      {run.status === "COMPLETED" && result && (
        <StatusIndicator type="success">
          Categorised by TrackStars — {matching} pattern
          {matching === 1 ? " explains" : "s explain"} {result.explainedPct}% of{" "}
          {result.totalItems.toLocaleString()} items (
          {run.completedBy ?? run.requestedBy},{" "}
          {formatRelativeTime(run.completedAt ?? run.requestedAt, now)})
        </StatusIndicator>
      )}
    </span>
  );
};

interface InvestigationTrackStarsTabProps {
  stage: Stage;
  investigation: Investigation;
  loading?: boolean;
  onAddLink: (link: InvestigationLinkInput) => void;
  /** Open the widen-rule dialog straight away (the page's "Review rule change…" prompt). */
  openWiden?: boolean;
  onWidenOpened?: () => void;
  onProposeConclusion: (proposal: ProposedConclusion) => void;
  currentUser?: string | null;
  /** Rename the investigation (the suggested title of a categorisation run). */
  onRename?: (title: string) => void;
}

/**
 * The TrackStars tab on an investigation: what TRACK's DLQ analysis says about this population —
 * the pattern it was started from and any other pattern its rule meets — with the report it
 * came from, the analysis as a card, and the two ways to adopt it (propose the conclusion, add
 * the evidence links). Nothing is written without a click.
 */
export const InvestigationTrackStarsTab: React.FC<
  InvestigationTrackStarsTabProps
> = ({
  stage,
  investigation,
  loading = false,
  onAddLink,
  openWiden,
  onWidenOpened,
  onProposeConclusion,
  currentUser,
  onRename,
}) => {
  const query = useInvestigationSuggestions(stage);
  const completedRunId = hasCompletedTrackStarsRun(investigation)
    ? investigation.trackstarsRun?.runId
    : undefined;
  const runQuery = useInvestigationTrackStarsRunPatterns(
    stage,
    investigation.id,
    completedRunId
  );
  // The case's own run: every pattern as a full card. Those matching none of its items are folded.
  const runCards = useMemo(
    () => (runQuery.data?.available ? runQuery.data.suggestions : []),
    [runQuery.data]
  );
  const runKeys = useMemo(
    () => new Set(runCards.map(suggestionKey)),
    [runCards]
  );
  const matches = useMemo(
    () =>
      patternsForInvestigation(
        query.data?.suggestions,
        investigation.id
      ).filter(
        // A pattern of the case's own run is shown once, as a run card.
        (m) => !runKeys.has(suggestionKey(m.suggestion))
      ),
    [query.data?.suggestions, investigation.id, runKeys]
  );
  const existingUrls = useMemo(
    () => new Set((investigation.links ?? []).map((l) => l.url)),
    [investigation.links]
  );
  const { origin } = investigation;
  const [widening, setWidening] = useState<InvestigationSuggestion | null>(
    null
  );
  const widenable = useMemo(
    () =>
      widenCandidates(
        investigation,
        matches.map((m) => m.suggestion)
      ),
    [investigation, matches]
  );
  useEffect(() => {
    if (openWiden && widenable.length) {
      setWidening(widenable[0]);
      onWidenOpened?.();
    }
  }, [openWiden, widenable, onWidenOpened]);
  // What needs the reader: the pattern the case came from, what already covers it, and a newer
  // finding it can widen to. Other overlapping patterns are listed, folded, below.
  // One card per failure: when a newer, wider version of the case's own pattern exists, it
  // replaces the card of the pattern the case was started from (named in the header line).
  const newer = widenable[0];
  const primary = matches.filter(
    ({ suggestion, relation }) =>
      (relation !== "partial" || widenable.includes(suggestion)) &&
      !(newer && relation === "origin")
  );
  const others = matches.filter(
    (m) => !primary.includes(m) && !(newer && m.relation === "origin")
  );
  const report = query.data?.report;
  // The stage report is only named when its patterns are on this tab (the run cards come from
  // the case's own run report).
  const showReport = !!report && (matches.length > 0 || !runCards.length);
  // Only the owner of an open case may set the conclusion (backend: owner-only, frozen when closed).
  const canEditConclusion =
    !!currentUser &&
    investigation.owner === currentUser &&
    !isTerminal(investigation.status);

  const renderPattern = ({
    suggestion,
    relation,
    note,
  }: {
    suggestion: InvestigationSuggestion;
    relation: string;
    note?: string;
  }) => {
    const missingLinks = (suggestion.starter?.links ?? []).filter(
      (link) => !existingUrls.has(link.url)
    );
    return (
      <div
        key={suggestionKey(suggestion)}
        data-testid={`trackstars-pattern-${suggestion.patternId}`}
      >
        {/* No reportUrl: the report is linked once, above. */}
        <TrackStarsSuggestionCard
          stage={stage}
          suggestion={suggestion}
          hideOwnership
          fromRun={relation === "run"}
          relationNote={
            note ??
            (widenable.includes(suggestion)
              ? "Latest version of this investigation's pattern"
              : RELATION_LABEL[relation] ?? relation)
          }
          headerAction={
            canEditConclusion || missingLinks.length ? (
              <ButtonDropdown
                variant="icon"
                ariaLabel="More actions"
                disabled={loading}
                expandToViewport
                items={[
                  ...(canEditConclusion
                    ? [{ id: "conclusion", text: "Use as proposed conclusion" }]
                    : []),
                  // One item per link: every add is a versioned update, so they cannot be batched.
                  ...missingLinks.map((link) => ({
                    id: `link:${link.url}`,
                    text: `Add evidence link: ${shortLinkLabel(
                      link.label ?? link.url
                    )}`,
                  })),
                ]}
                onItemClick={({ detail }) => {
                  if (detail.id === "conclusion") {
                    onProposeConclusion(proposalFor(suggestion));
                    return;
                  }
                  const link = missingLinks.find(
                    (l) => `link:${l.url}` === detail.id
                  );
                  if (link) onAddLink({ ...link, source: "trackstars" });
                }}
              />
            ) : undefined
          }
        />
      </div>
    );
  };

  return (
    <Container
      data-testid="trackstars-tab"
      header={
        <Header
          variant="h3"
          description="What TRACK's multi-agent DLQ analysis says about this population."
          actions={
            hasCompletedTrackStarsRun(investigation) ? (
              <RunTrackStarsButton
                stage={stage}
                investigation={investigation}
                rerun
              />
            ) : undefined
          }
        >
          TrackStars
        </Header>
      }
    >
      <SpaceBetween size="m">
        <TrackStarsRunStatus investigation={investigation} />
        <TrackStarsRunResult
          stage={stage}
          investigation={investigation}
          currentUser={currentUser}
          loading={loading}
          onRename={onRename}
        />
        {runQuery.isFetching && !runQuery.data && (
          <StatusIndicator type="loading">
            Loading the patterns of this run…
          </StatusIndicator>
        )}
        {runCards
          .filter((s) => runPatternItems(s) > 0)
          .map((suggestion) =>
            renderPattern({
              suggestion,
              relation: "run",
              note: runPatternNote(suggestion),
            })
          )}
        {runCards.some((s) => runPatternItems(s) === 0) && (
          <ExpandableSection
            headerText={`Patterns of this run that match none of these items (${
              runCards.filter((s) => runPatternItems(s) === 0).length
            })`}
            variant="footer"
          >
            <SpaceBetween size="m">
              {runCards
                .filter((s) => runPatternItems(s) === 0)
                .map((suggestion) =>
                  renderPattern({
                    suggestion,
                    relation: "run",
                    note: "From this investigation's run",
                  })
                )}
            </SpaceBetween>
          </ExpandableSection>
        )}
        {query.isLoading && (
          <StatusIndicator type="loading">
            Loading the latest report…
          </StatusIndicator>
        )}
        {!query.isLoading && query.data && !query.data.available && (
          <Alert type="info">
            No TrackStars report has been imported for {stage} yet.
          </Alert>
        )}
        {(origin || showReport) && (
          <Box color="text-body-secondary" fontSize="body-s">
            {origin && (
              <>
                Started from {shortPatternId(origin.patternId, origin.reportId)}
                {newer
                  ? ` → updated ${shortPatternId(
                      newer.patternId,
                      suggestionReportId(newer)
                    )}`
                  : ""}
                {showReport ? " · " : ""}
              </>
            )}
            {showReport && report && (
              <>
                Report{" "}
                {earlierReportLabel({
                  reportId: report.reportId,
                  generatedAt: report.generatedAt,
                })}
                {report.stale ? " (older than a week)" : ""} ·{" "}
                <SafeExternalLink href={report.reportUrl}>
                  Open report
                </SafeExternalLink>
              </>
            )}
          </Box>
        )}
        {newer && canEditConclusion && (
          <div
            data-testid="trackstars-widen-prompt"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <Box>
              The latest report found this failure beyond your rule
              {newer.unownedCount
                ? ` (about ${newer.unownedCount.toLocaleString()} items your rule leaves out)`
                : ""}
              .
            </Box>
            <Button
              variant="primary"
              disabled={loading}
              onClick={() => setWidening(newer)}
            >
              Widen rule…
            </Button>
          </div>
        )}
        {/* The investigation's own run answers this; the stage-wide report is only a fallback. */}
        {!query.isLoading &&
          query.data?.available &&
          matches.length === 0 &&
          !hasCompletedTrackStarsRun(investigation) && (
            <Alert type="info">
              The latest report has no pattern matching this population. Run
              TrackStars on it to get a root-cause analysis.
            </Alert>
          )}
        {primary.map(renderPattern)}
        {others.length > 0 && (
          <ExpandableSection
            headerText={`Other patterns that overlap this investigation (${others.length})`}
            variant="footer"
          >
            <SpaceBetween size="m">{others.map(renderPattern)}</SpaceBetween>
          </ExpandableSection>
        )}
      </SpaceBetween>
      {widening && (
        <WidenRuleModal
          stage={stage}
          investigation={investigation}
          rule={widening.derivedRule ?? []}
          label={`TrackStars ${shortPatternId(
            widening.patternId,
            suggestionReportId(widening)
          )}`}
          patternId={widening.patternId}
          category={widening.suggestedCategory}
          visible
          onDismiss={() => setWidening(null)}
        />
      )}
    </Container>
  );
};

export default InvestigationTrackStarsTab;
